import { useState, useEffect, useRef } from 'react';
import { api } from '../../shared/axiosInstance';
import MetricRegistry from '../registry/MetricRegistry';

// Compute default pinned cards from registry entries with defaultPinned: true
const DEFAULT_PINNED = Object.entries(MetricRegistry)
  .filter(([, entry]) => entry.defaultPinned === true)
  .map(([metricId]) => metricId);

export function useDashboardPrefs() {
  const [pinnedCards, setPinnedCardsState] = useState(DEFAULT_PINNED);
  const [appliedPreset, setAppliedPresetState] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const saveTimer = useRef(null);

  // Load preferences on mount
  useEffect(() => {
    api.get('/admin/dashboard-prefs')
      .then((res) => {
        const prefs = res.data || {};
        if (Array.isArray(prefs.pinnedCards) && prefs.pinnedCards.length > 0) {
          setPinnedCardsState(prefs.pinnedCards);
        } else {
          setPinnedCardsState(DEFAULT_PINNED);
        }
        setAppliedPresetState(prefs.appliedPreset || null);
      })
      .catch(() => {
        // Fall back to defaults on error
        setPinnedCardsState(DEFAULT_PINNED);
        setAppliedPresetState(null);
      })
      .finally(() => {
        setLoaded(true);
      });
  }, []);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      clearTimeout(saveTimer.current);
    };
  }, []);

  // Internal debounced save — accepts explicit values to avoid stale closure
  const debouncedSave = (cards, preset) => {
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      api.put('/admin/dashboard-prefs', { pinnedCards: cards, appliedPreset: preset });
    }, 500);
  };

  // Update pinned cards and trigger debounced save
  const setPinnedCards = (cards) => {
    setPinnedCardsState(cards);
    setAppliedPresetState((currentPreset) => {
      debouncedSave(cards, currentPreset);
      return currentPreset;
    });
  };

  // Update both preset and pinned cards, then trigger debounced save
  const setAppliedPreset = (presetId, newCards) => {
    setPinnedCardsState(newCards);
    setAppliedPresetState(presetId);
    debouncedSave(newCards, presetId);
  };

  // Reset to registry defaults, clear preset, and save
  const resetToDefaults = () => {
    setPinnedCardsState(DEFAULT_PINNED);
    setAppliedPresetState(null);
    debouncedSave(DEFAULT_PINNED, null);
  };

  return {
    pinnedCards,
    appliedPreset,
    loaded,
    setPinnedCards,
    setAppliedPreset,
    resetToDefaults,
  };
}
